import { useState, useEffect } from 'react'
import moment from 'moment'
import { fetchData } from '../../../../../../global/utils/fetch'
import { getLocationById } from '../../../api'
import {
  getTechniciansWithSchedulingData,
  checkSchedulePatternChanges,
  saveSchedulePattern,
} from '../api'
import { ILocation } from '../../../../../../../../app/entities/Location'
import { ITechnician } from '../../../../../../../../app/entities/Technician'
import {
  ISchedulePattern,
  WeekPattern,
} from '../../../../../../../../app/entities/SchedulePattern'
import { getCalendarDateRange } from '../utils/scheduling-utils'
import { getActiveSchedulePattern } from '../../../../../../global/utils/schedule-patterns'
import { editTech } from '../../../../../../technicians/components/technician-modal/api'
import { DayAnalysisResult } from '../../../../../../../../app/types/technician-impact-analysis'
import isDayOptimized from '../../../../../../global/utils/is-day-optimized'

export interface TechnicianStatus {
  technicianObjectId: string
  name: string
  initials: string
  status: 'working' | 'off' | 'holiday' | 'unassigned'
  isHumanTouched: boolean
  holidayName?: string // e.g., "Christmas", "Thanksgiving"
}

interface UseSchedulingDataOptions {
  isOpen: boolean
  locationObjectId: string
  technicianObjectId?: string
  calendarDate: Date
}

export const useSchedulingData = ({
  isOpen,
  locationObjectId,
  technicianObjectId,
  calendarDate,
}: UseSchedulingDataOptions) => {
  // State
  const [location, setLocation] = useState<ILocation | null>(null)
  const [techniciansWithScheduling, setTechniciansWithScheduling] = useState<
    ITechnician[]
  >([])
  const [loading, setLoading] = useState(false)
  const [technicianStatuses, setTechnicianStatuses] = useState<
    Map<string, TechnicianStatus[]>
  >(new Map())
  const [visibleTechnicianIds, setVisibleTechnicianIds] = useState<Set<string>>(
    new Set(),
  )

  // Schedule editing state
  const [expandedScheduleEditors, setExpandedScheduleEditors] = useState<
    Set<string>
  >(new Set())
  const [editingSchedules, setEditingSchedules] = useState<
    Map<
      string,
      {
        effectiveFrom: string
        reason: string
        originalPattern: ISchedulePattern | null
      }
    >
  >(new Map())
  const [draftSchedulePatterns, setDraftSchedulePatterns] = useState<
    Map<string, ISchedulePattern>
  >(new Map())
  const [checkResults, setCheckResults] = useState<
    Map<string, DayAnalysisResult[]>
  >(new Map())

  // Impact modal state
  const [impactModalOpen, setImpactModalOpen] = useState(false)
  const [impactModalData, setImpactModalData] = useState<{
    technicianObjectId: string
    technicianName: string
    checkResult: DayAnalysisResult[]
  } | null>(null)

  // Loading states
  const [checkingChanges, setCheckingChanges] = useState(false)
  const [savingPattern, setSavingPattern] = useState(false)

  // Fetch location and technician data
  useEffect(() => {
    if (isOpen && locationObjectId) {
      setLoading(true)

      // Fetch location data
      fetchData(getLocationById(locationObjectId))
        .then((locationData) => {
          setLocation(locationData as ILocation)
        })
        .catch((error) => {
          console.error('Error fetching location:', error)
        })

      // Fetch scheduling data
      fetchSchedulingData()
    }
  }, [isOpen, locationObjectId])

  // Re-fetch when calendar date changes
  useEffect(() => {
    if (isOpen && locationObjectId) {
      fetchSchedulingData()
    }
  }, [calendarDate])

  // Fetch scheduling data (SchedulePatterns and DaySettings)
  const fetchSchedulingData = async () => {
    try {
      const { startDate, endDate } = getCalendarDateRange(calendarDate)

      // Fetch technicians with their scheduling data for visible date range only
      const techniciansData = (await fetchData(
        getTechniciansWithSchedulingData(locationObjectId, startDate, endDate),
      )) as ITechnician[]

      // Sort technicians alphabetically by name
      const sortedTechnicians = [...techniciansData].sort((a, b) =>
        (a.name || '').localeCompare(b.name || ''),
      )
      setTechniciansWithScheduling(sortedTechnicians)

      // Set visibility: all enabled for location view, single for technician view
      if (technicianObjectId) {
        setVisibleTechnicianIds(new Set([technicianObjectId]))
      } else {
        setVisibleTechnicianIds(
          new Set(sortedTechnicians.map((t) => t.objectId)),
        )
      }

      // Process the data into status format
      processSchedulingData(sortedTechnicians, startDate, endDate)
    } catch (error) {
      console.error('Error fetching scheduling data:', error)
      setTechniciansWithScheduling([])
      setTechnicianStatuses(new Map())
    } finally {
      setLoading(false)
    }
  }

  // Process scheduling data into technician statuses
  const processSchedulingData = (
    techsWithScheduling: ITechnician[],
    requestedStartDate: string,
    requestedEndDate: string,
  ) => {
    const statusMap = new Map<string, TechnicianStatus[]>()

    // Get all unique dates from the actual API data
    const allDatesInData = new Set<string>()
    techsWithScheduling.forEach((tech) => {
      tech.daySettings.forEach((ds) => {
        allDatesInData.add(moment.utc(ds.day).format('YYYY-MM-DD'))
      })
    })

    // Also include the full requested date range (for days with no DaySettings)
    const start = moment(requestedStartDate)
    const end = moment(requestedEndDate)
    const current = start.clone()
    while (current.isSameOrBefore(end)) {
      allDatesInData.add(current.format('YYYY-MM-DD'))
      current.add(1, 'day')
    }

    // Process only the dates we actually have or need
    Array.from(allDatesInData).forEach((dateKey) => {
      const dayStatuses = techsWithScheduling.map((techData) => {
        // Check if there's a DaySetting for this technician and date
        const daySetting = techData.daySettings.find((ds) => {
          const dbDateOnly = moment.utc(ds.day).format('YYYY-MM-DD')
          return dbDateOnly === dateKey
        })

        if (daySetting) {
          let status: TechnicianStatus['status'] = 'unassigned'
          let holidayName: string | undefined

          if (daySetting.workDay === true) {
            status = 'working'
          } else if (daySetting.workDay === false) {
            status = 'off'
          }

          // Check for holiday - support both old format "Holiday" and new format "Holiday: Name"
          if (daySetting.reason?.startsWith('Holiday')) {
            status = 'holiday'
            // Extract holiday name from "Holiday: Christmas" format
            const colonIndex = daySetting.reason.indexOf(':')
            if (colonIndex !== -1) {
              holidayName = daySetting.reason.substring(colonIndex + 1).trim()
            }
          }

          return {
            technicianObjectId: techData.objectId,
            name: techData.name || 'Unknown',
            initials:
              techData.name
                ?.split(' ')
                .map((n) => n[0])
                .join('')
                .slice(0, 2) || 'T',
            status,
            isHumanTouched: !!daySetting.humanUpdatedTimestamp,
            holidayName,
          }
        } else {
          return null
        }
      })

      // Filter out null values
      const validDayStatuses = dayStatuses.filter(
        (status) => status !== null,
      ) as TechnicianStatus[]

      statusMap.set(dateKey, validDayStatuses)
    })

    setTechnicianStatuses(statusMap)
  }

  // Toggle technician visibility
  const toggleTechnicianVisibility = (technicianObjectId: string) => {
    const newVisible = new Set(visibleTechnicianIds)
    if (newVisible.has(technicianObjectId)) {
      newVisible.delete(technicianObjectId)
    } else {
      newVisible.add(technicianObjectId)
    }
    setVisibleTechnicianIds(newVisible)
  }

  // Schedule editor management
  const toggleScheduleEditor = (technicianObjectId: string) => {
    const newExpanded = new Set(expandedScheduleEditors)
    if (newExpanded.has(technicianObjectId)) {
      newExpanded.delete(technicianObjectId)
      // If hiding the schedule editor, also cancel any editing state
      if (editingSchedules.has(technicianObjectId)) {
        cancelEditingSchedule(technicianObjectId)
      }
    } else {
      newExpanded.add(technicianObjectId)
    }
    setExpandedScheduleEditors(newExpanded)
  }

  const startEditingSchedule = (technicianObjectId: string) => {
    // Find the technician and get their active schedule pattern
    const technician = techniciansWithScheduling.find(
      (t) => t.objectId === technicianObjectId,
    )
    if (!technician) return

    const currentActivePattern = getActiveSchedulePattern(
      technician.schedulePatterns || [],
    )

    const newEditingMap = new Map(editingSchedules)
    const hasRouteOptimizationRan = isDayOptimized()
    newEditingMap.set(technicianObjectId, {
      effectiveFrom: moment()
        .add(hasRouteOptimizationRan ? 2 : 1, 'day')
        .format('YYYY-MM-DD'),
      reason: '',
      originalPattern: currentActivePattern,
    })
    setEditingSchedules(newEditingMap)

    // Create draft pattern from current active pattern
    const newDraftMap = new Map(draftSchedulePatterns)
    if (currentActivePattern) {
      // Clone the current active pattern for editing
      newDraftMap.set(technicianObjectId, {
        ...currentActivePattern,
        // Don't copy these - they'll be set when saving
        effectiveFrom: moment().add(1, 'day').toDate(),
        reason: '',
      })
    } else {
      // Create default schedule pattern with 2-week pattern
      const defaultPattern: ISchedulePattern = {
        objectId: '',
        created: 0,
        updated: 0,
        updatedBy: '',
        weekPatterns: [
          { week: 0, days: [] },
          { week: 1, days: [] },
        ],
        effectiveFrom: moment().add(1, 'day').toDate(),
        reason: '',
        workDayFrom: '08:00',
        workDayTo: '17:00',
        lunchDuration: 60,
      }
      newDraftMap.set(technicianObjectId, defaultPattern)
    }
    setDraftSchedulePatterns(newDraftMap)
  }

  const cancelEditingSchedule = (technicianObjectId: string) => {
    const newEditingMap = new Map(editingSchedules)
    const newDraftMap = new Map(draftSchedulePatterns)
    const newCheckMap = new Map(checkResults)

    newEditingMap.delete(technicianObjectId)
    newDraftMap.delete(technicianObjectId)
    newCheckMap.delete(technicianObjectId)

    setEditingSchedules(newEditingMap)
    setDraftSchedulePatterns(newDraftMap)
    setCheckResults(newCheckMap)
  }

  const updateEditingData = (
    technicianObjectId: string,
    field: 'effectiveFrom' | 'reason',
    value: string,
  ) => {
    const newEditingMap = new Map(editingSchedules)
    const current = newEditingMap.get(technicianObjectId)
    if (current) {
      newEditingMap.set(technicianObjectId, {
        ...current,
        [field]: value,
      })
      setEditingSchedules(newEditingMap)
    }
  }

  const toggleDayInDraft = (
    technicianObjectId: string,
    weekNumber: number,
    dayName: string,
  ) => {
    const newDraftMap = new Map(draftSchedulePatterns)
    const currentDraft = newDraftMap.get(technicianObjectId)

    if (currentDraft && currentDraft.weekPatterns) {
      const updatedWeekPatterns = currentDraft.weekPatterns.map(
        (week: WeekPattern) => {
          if (week.week === weekNumber) {
            const newDays = week.days.includes(dayName)
              ? week.days.filter((d: string) => d !== dayName)
              : [...week.days, dayName]
            return { ...week, days: newDays }
          }
          return week
        },
      )

      newDraftMap.set(technicianObjectId, {
        ...currentDraft,
        weekPatterns: updatedWeekPatterns,
      })
      setDraftSchedulePatterns(newDraftMap)
    }
  }

  const addWeekToDraft = (technicianObjectId: string) => {
    const newDraftMap = new Map(draftSchedulePatterns)
    const currentDraft = newDraftMap.get(technicianObjectId)

    if (currentDraft && currentDraft.weekPatterns) {
      const newWeekNumber =
        Math.max(...currentDraft.weekPatterns.map((w: WeekPattern) => w.week)) +
        1
      const updatedWeekPatterns = [
        ...currentDraft.weekPatterns,
        { week: newWeekNumber, days: [] },
      ]
      newDraftMap.set(technicianObjectId, {
        ...currentDraft,
        weekPatterns: updatedWeekPatterns,
      })
      setDraftSchedulePatterns(newDraftMap)
    }
  }

  const removeWeekFromDraft = (
    technicianObjectId: string,
    weekNumber: number,
  ) => {
    const newDraftMap = new Map(draftSchedulePatterns)
    const currentDraft = newDraftMap.get(technicianObjectId)

    if (
      currentDraft &&
      currentDraft.weekPatterns &&
      currentDraft.weekPatterns.length > 1
    ) {
      const updatedWeekPatterns = currentDraft.weekPatterns
        .filter((w: WeekPattern) => w.week !== weekNumber)
        .map((week: WeekPattern, index: number) => ({ ...week, week: index }))

      newDraftMap.set(technicianObjectId, {
        ...currentDraft,
        weekPatterns: updatedWeekPatterns,
      })
      setDraftSchedulePatterns(newDraftMap)
    }
  }

  // Check changes handler
  const handleCheckChanges = async (
    technicianObjectId: string,
    workTimeData?: Pick<
      ITechnician,
      'workDayFrom' | 'workDayTo' | 'lunchDuration'
    >,
    overrideEffectiveDate?: string, // Allow direct date override to avoid state timing issues
  ) => {
    setCheckingChanges(true)
    try {
      const editingData = editingSchedules.get(technicianObjectId)
      const draftPattern = draftSchedulePatterns.get(technicianObjectId)

      if (!editingData || !draftPattern) return

      // Find the technician to get current work times
      const technician = techniciansWithScheduling.find(
        (t) => t.objectId === technicianObjectId,
      )

      // Use work time data if provided, otherwise fall back to technician's current settings or schedule pattern
      const activePattern = getActiveSchedulePattern(
        technician?.schedulePatterns || [],
      )
      const workTimes = workTimeData || {
        workDayFrom: activePattern?.workDayFrom || technician?.workDayFrom,
        workDayTo: activePattern?.workDayTo || technician?.workDayTo,
        lunchDuration:
          activePattern?.lunchDuration || technician?.lunchDuration,
      }

      const effectiveDate = overrideEffectiveDate || editingData.effectiveFrom
      const schedulePatternData = {
        effectiveFrom: new Date(effectiveDate),
        reason: editingData.reason,
        weekPatterns: draftPattern.weekPatterns,
        workDayFrom: workTimes.workDayFrom,
        workDayTo: workTimes.workDayTo,
        lunchDuration: workTimes.lunchDuration,
      }

      // Update the draft pattern with the new work times for UI display
      const updatedDraftPattern = {
        ...draftPattern,
        workDayFrom: workTimes.workDayFrom || '09:00',
        workDayTo: workTimes.workDayTo || '18:00',
        lunchDuration: workTimes.lunchDuration || 30,
      }
      const newDraftMap = new Map(draftSchedulePatterns)
      newDraftMap.set(technicianObjectId, updatedDraftPattern)
      setDraftSchedulePatterns(newDraftMap)

      const checkResult = (await fetchData(
        checkSchedulePatternChanges(technicianObjectId, schedulePatternData),
      )) as DayAnalysisResult[]

      const newCheckMap = new Map(checkResults)
      newCheckMap.set(technicianObjectId, checkResult)
      setCheckResults(newCheckMap)

      // Find technician name and show impact modal
      const technicianName = technician?.name || 'Unknown Technician'

      setImpactModalData({
        technicianObjectId,
        technicianName,
        checkResult,
      })
      setImpactModalOpen(true)
    } catch (error) {
      console.error('Error checking schedule changes:', error)
    } finally {
      setCheckingChanges(false)
    }
  }

  // Save work times (for technician-level work time changes)
  const handleSaveWorkTimes = async (
    technicianObjectId: string,
    workTimeData: Pick<
      ITechnician,
      'workDayFrom' | 'workDayTo' | 'lunchDuration'
    >,
  ) => {
    try {
      const updatePayload = {
        objectId: technicianObjectId,
        ...workTimeData,
      }
      await fetchData(editTech(updatePayload))

      // Refresh data to show updated work times
      const { startDate, endDate } = getCalendarDateRange(calendarDate)
      const techniciansData = (await fetchData(
        getTechniciansWithSchedulingData(locationObjectId, startDate, endDate),
      )) as ITechnician[]

      const sortedTechnicians = [...techniciansData].sort((a, b) =>
        (a.name || '').localeCompare(b.name || ''),
      )
      setTechniciansWithScheduling(sortedTechnicians)
      processSchedulingData(sortedTechnicians, startDate, endDate)

      return { success: true }
    } catch (error) {
      console.error('Error saving work times:', error)
      throw error
    }
  }

  // Impact modal handlers
  const handleImpactModalClose = () => {
    setImpactModalOpen(false)
    setImpactModalData(null)
  }

  const handleConfirmOriginalDate = async (
    overrideManualSettings: string[],
  ) => {
    if (!impactModalData) return

    setSavingPattern(true)
    try {
      const { technicianObjectId } = impactModalData
      const editingData = editingSchedules.get(technicianObjectId)
      const draftPattern = draftSchedulePatterns.get(technicianObjectId)

      if (!editingData || !draftPattern) return

      // Find the technician to get current work times
      const technician = techniciansWithScheduling.find(
        (t) => t.objectId === technicianObjectId,
      )

      // Use the same work time logic as in handleCheckChanges
      const activePattern = getActiveSchedulePattern(
        technician?.schedulePatterns || [],
      )
      const workTimes = {
        workDayFrom:
          draftPattern.workDayFrom ||
          activePattern?.workDayFrom ||
          technician?.workDayFrom,
        workDayTo:
          draftPattern.workDayTo ||
          activePattern?.workDayTo ||
          technician?.workDayTo,
        lunchDuration:
          draftPattern.lunchDuration ||
          activePattern?.lunchDuration ||
          technician?.lunchDuration,
      }

      const schedulePatternData = {
        effectiveFrom: new Date(editingData.effectiveFrom),
        reason: editingData.reason,
        weekPatterns: draftPattern.weekPatterns,
        workDayFrom: workTimes.workDayFrom,
        workDayTo: workTimes.workDayTo,
        lunchDuration: workTimes.lunchDuration,
      }

      // Call the save schedule pattern API with manual overrides
      await fetchData(
        saveSchedulePattern(
          technicianObjectId,
          schedulePatternData,
          overrideManualSettings,
        ),
      )

      // Clear editing state and close modal
      const newEditingMap = new Map(editingSchedules)
      newEditingMap.delete(technicianObjectId)
      setEditingSchedules(newEditingMap)

      const newDraftMap = new Map(draftSchedulePatterns)
      newDraftMap.delete(technicianObjectId)
      setDraftSchedulePatterns(newDraftMap)

      // Refresh data to show the new schedule pattern
      await fetchSchedulingData()

      handleImpactModalClose()
    } catch (error) {
      console.error('Error saving schedule pattern:', error)
    } finally {
      setSavingPattern(false)
    }
  }

  const handleUseSafeDate = async (safeDate: string) => {
    if (!impactModalData) return

    const { technicianObjectId } = impactModalData
    const editingData = editingSchedules.get(technicianObjectId)
    const draftPattern = draftSchedulePatterns.get(technicianObjectId)

    if (!editingData || !draftPattern) return

    // Update the effective date to the safe date
    const updatedEditingData = {
      ...editingData,
      effectiveFrom: safeDate,
    }

    const newEditingMap = new Map(editingSchedules)
    newEditingMap.set(technicianObjectId, updatedEditingData)
    setEditingSchedules(newEditingMap)

    // Close current modal
    handleImpactModalClose()

    // Re-run check changes with the new effective date
    // Find the technician to get current work times for consistency
    const technician = techniciansWithScheduling.find(
      (t) => t.objectId === technicianObjectId,
    )
    const activePattern = getActiveSchedulePattern(
      technician?.schedulePatterns || [],
    )

    const workTimes = {
      workDayFrom:
        draftPattern.workDayFrom ||
        activePattern?.workDayFrom ||
        technician?.workDayFrom ||
        '09:00',
      workDayTo:
        draftPattern.workDayTo ||
        activePattern?.workDayTo ||
        technician?.workDayTo ||
        '18:00',
      lunchDuration:
        draftPattern.lunchDuration ||
        activePattern?.lunchDuration ||
        technician?.lunchDuration ||
        30,
    }

    // Trigger check changes with updated effective date and current work times
    await handleCheckChanges(technicianObjectId, workTimes, safeDate)
  }

  return {
    // Data
    location,
    techniciansWithScheduling,
    loading,
    technicianStatuses,
    visibleTechnicianIds,

    // Schedule editing state
    expandedScheduleEditors,
    editingSchedules,
    draftSchedulePatterns,
    checkResults,

    // Impact modal state
    impactModalOpen,
    impactModalData,

    // Loading states
    checkingChanges,
    savingPattern,

    // Actions
    toggleTechnicianVisibility,
    toggleScheduleEditor,
    startEditingSchedule,
    cancelEditingSchedule,
    updateEditingData,
    toggleDayInDraft,
    addWeekToDraft,
    removeWeekFromDraft,
    handleCheckChanges,
    handleSaveWorkTimes,
    fetchSchedulingData,

    // Impact modal actions
    handleImpactModalClose,
    handleConfirmOriginalDate,
    handleUseSafeDate,
  }
}
